import moment from 'moment'
import {
  HOUR_MINUTE_AM_PM,
  MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT,
  TODAY,
  YESTERDAY,
} from '@src/config/momentFormat'
import { NotificationRecord } from '@src/models/notifications/types'

export type NotificationInboxSectionKey = 'today' | 'yesterday' | 'last7Days' | 'older'

export interface NotificationInboxSection {
  data: NotificationRecord[]
  key: NotificationInboxSectionKey
  title: string
}

const SECTION_CONFIG: Array<Pick<NotificationInboxSection, 'key' | 'title'>> = [
  { key: 'today', title: TODAY },
  { key: 'yesterday', title: YESTERDAY },
  { key: 'last7Days', title: 'Last 7 Days' },
  { key: 'older', title: 'Older' },
]

const getCalendarDayDifference = (createdAt: moment.MomentInput, now: moment.Moment) => {
  const createdAtMoment = moment.isMoment(createdAt) ? createdAt : moment(createdAt)

  return now.clone().startOf('day').diff(createdAtMoment.clone().startOf('day'), 'days')
}

export const getNotificationDateBucket = (
  createdAt: moment.MomentInput,
  now: moment.Moment = moment(),
): NotificationInboxSectionKey => {
  const dayDifference = getCalendarDayDifference(createdAt, now)

  if (dayDifference <= 0) {
    return 'today'
  }

  if (dayDifference === 1) {
    return 'yesterday'
  }

  if (dayDifference <= 7) {
    return 'last7Days'
  }

  return 'older'
}

export const formatCompactRelativeTime = (createdAt: string, now: moment.Moment = moment()) => {
  const createdAtMoment = moment(createdAt)
  const minutesAgo = Math.max(now.diff(createdAtMoment, 'minutes'), 0)

  if (minutesAgo < 60) {
    return `${Math.max(minutesAgo, 1)}m ago`
  }

  return `${Math.max(now.diff(createdAtMoment, 'hours'), 1)}h ago`
}

export const formatNotificationTimestamp = (createdAt: string, now: moment.Moment = moment()) => {
  const createdAtMoment = moment(createdAt)
  const bucket = getNotificationDateBucket(createdAt, now)

  switch (bucket) {
    case 'today':
      return formatCompactRelativeTime(createdAt, now)
    case 'yesterday':
      return createdAtMoment.format(HOUR_MINUTE_AM_PM)
    case 'last7Days':
      return createdAtMoment.format('ddd')
    case 'older':
    default:
      return createdAtMoment.format(MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT)
  }
}

export const groupNotificationsByDate = (
  notifications: NotificationRecord[],
  now: moment.Moment = moment(),
): NotificationInboxSection[] => {
  const notificationsWithTimestamp = notifications
    .map((notification) => {
      const createdAtMoment = moment(notification.createdAt)

      return {
        createdAtMoment,
        notification,
        timestamp: createdAtMoment.valueOf(),
      }
    })
    .sort((left, right) => right.timestamp - left.timestamp)

  const buckets = notificationsWithTimestamp.reduce<
    Record<NotificationInboxSectionKey, NotificationRecord[]>
  >(
    (result, { notification, createdAtMoment }) => {
      const key = getNotificationDateBucket(createdAtMoment, now)
      result[key].push(notification)
      return result
    },
    {
      last7Days: [],
      older: [],
      today: [],
      yesterday: [],
    },
  )

  return SECTION_CONFIG.map((sectionConfig) => ({
    ...sectionConfig,
    data: buckets[sectionConfig.key],
  })).filter((section) => section.data.length > 0)
}
